export type FileContext = {
  botId: number;
  key: string;
  password: string;
  bucket: string;
  apiUrl?: string;
} & (
  | { messageId: number; productId?: never; shared?: never }
  | { productId: number; messageId?: never; shared?: never }
  | { shared: true; messageId?: never; productId?: never }
);

type Grant = { url: string; expiresAt: number };
const grants = new Map<string, Grant>();
const pending = new Map<string, Promise<string>>();
let generation = 0;

export function clearFileUrlCache(): void {
  generation++;
  grants.clear();
  pending.clear();
}

export function storageObject(url: string, bucket: string): string | null {
  const parts = /^https:\/\/([^/?#]+)(\/[^?#]*)?/.exec(url);
  if (!parts || !bucket) return null;
  const host = parts[1].toLowerCase();
  const prefix = host === "firebasestorage.googleapis.com" ? `/v0/b/${bucket}/o/`
    : host === "storage.googleapis.com" ? `/${bucket}/`
    : host === `${bucket}.storage.googleapis.com` ? "/" : null;
  if (!prefix || !parts[2]?.startsWith(prefix)) return null;
  const object = parts[2].slice(prefix.length);
  try { return decodeURIComponent(object); } catch { return object; }
}

/** Original URLs stay in records. Credentials go only in the backend POST body. */
export async function getFileUrl(url: string, context: FileContext, refresh = false): Promise<string> {
  const object = storageObject(url, context.bucket);
  if (object === null) return url;
  const { botId, key, password } = context;
  const resource = context.shared ? "files" : context.productId !== undefined ? `catalog/products/${context.productId}` : `messages/${context.messageId}`;
  const recordId = context.shared ? true : context.productId ?? context.messageId;
  const apiUrl = (context.apiUrl || process.env.REACT_APP_CHATHUB_APP_URL || "").replace(/\/$/, "");
  if (!botId || !recordId || !key || !password || !apiUrl) throw new Error("File access unavailable");
  const cacheKey = JSON.stringify([apiUrl, botId, key, password, resource, context.bucket, object]);
  const cached = grants.get(cacheKey);
  if (!refresh && cached && cached.expiresAt > Date.now() + 30000) return cached.url;
  const inFlight = pending.get(cacheKey);
  if (inFlight) return inFlight;
  grants.delete(cacheKey);
  const started = generation;
  const request = (async () => {
    const response = await fetch(`${apiUrl}/api/${botId}/${resource}/${context.shared ? "url" : "file"}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ key, password, url: url.split(/[?#]/, 1)[0] }),
    });
    if (!response.ok) throw new Error(`File access request failed (HTTP ${response.status})`);
    const data = await response.json().catch(() => { throw new Error("Invalid file access response: expected JSON"); });
    if (typeof data?.url !== "string") throw new Error("Invalid file access response: missing file URL");
    if (storageObject(data.url, context.bucket) !== object) throw new Error("Invalid file access response: URL does not match the requested file");
    // Expiry controls cache reuse only; the browser's clock must not reject a server-issued URL.
    const expiresAt = Date.parse(data.expiresAt);
    if (generation === started && Number.isFinite(expiresAt)) {
      // Keep browsing long histories from retaining an unbounded grant cache.
      if (grants.size >= 500) grants.delete(grants.keys().next().value!);
      grants.set(cacheKey, { url: data.url, expiresAt });
    }
    return data.url as string;
  })();
  pending.set(cacheKey, request);
  try { return await request; } finally {
    if (pending.get(cacheKey) === request) pending.delete(cacheKey);
  }
}

const urlFields = new Set(["url", "image_url", "imageUrl"]);

export function collectFileUrls(value: any, bucket: string): string[] {
  const urls = new Map<string, string>();
  const visit = (node: any) => {
    if (!node || typeof node !== "object") return;
    for (const [key, child] of Object.entries(node)) {
      if (typeof child === "string" && urlFields.has(key)) {
        const object = storageObject(child, bucket);
        if (object !== null) urls.set(object, child);
      } else visit(child);
    }
  };
  visit(value);
  return [...urls.values()];
}

/** Display copy only: an unresolved private URL stays blank, never public. */
export function withFileUrls<T>(value: T, bucket: string, urls: Map<string, string>): T {
  const visit = (node: any): any => {
    if (!node || typeof node !== "object") return node;
    if (Array.isArray(node)) return node.map(visit);
    const copy = Object.fromEntries(Object.entries(node).map(([key, child]) => {
      const object = typeof child === "string" && urlFields.has(key) ? storageObject(child, bucket) : null;
      return [key, object === null ? visit(child) : urls.get(object) || null];
    }));
    if (typeof node.payload?.url === "string") {
      const object = storageObject(node.payload.url, bucket);
      if (object !== null) {
        copy.payload.file_name = object.split("/").pop();
        if (/\.(svg|html?|xhtml|xml|js)$/i.test(object)) copy.type = "file";
      }
    }
    return copy;
  };
  return visit(value);
}
